/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { openDB } from "idb";
import type { IDBPDatabase } from "idb";

import { Log } from "../logging/Log";
import {
  DB_NAME,
  DB_VERSION,
  STORE_NAME,
  VALID_TILL_INDEX,
  EVICT_TILL_INDEX
} from "./storageConfig";

/**
 * Record shape stored in the IndexedDB object store.
 *
 * Every record follows this structure regardless of whether it is plain
 * or checksummed — plain records simply have `checksum` set to `null`.
 *
 * | Field      | Type             | Description                                      |
 * |------------|------------------|--------------------------------------------------|
 * | key        | string           | Unique identifier (e.g. "tokens", "epg_2026_03_16") |
 * | data       | unknown          | The cached payload                                |
 * | checksum   | string \| null   | SHA-256 hex digest (checksummed) or null (plain)  |
 * | validTill  | number           | Expiry timestamp (ms since epoch)                 |
 */
export interface TStorageRecord {
  /**
   * Unique record key (e.g. "tokens", "channels", "epg_2026_03_16").
   */
  key: string;

  /**
   * The cached data payload.
   */
  data: unknown;

  /**
   * SHA-256 hex digest of JSON.stringify(data) for checksummed records,
   * or null for plain records.
   */
  checksum: string | null;

  /**
   * Expiry timestamp in milliseconds since epoch.
   * Computed at write time as Date.now() + ttl.
   */
  validTill: number;

  evictTill?: number; // Optional field for eviction timestamp, used for cleanup
}

const TAG: string = "storageDatabase";

/**
 * Open (or create) the IndexedDB database used by the storage adapter.
 *
 * On first run the `upgrade` callback creates the single object store
 * with a `key` keyPath and a `evictTill` index for efficient expired-record
 * lookups during the startup cleanup pass.
 *
 * @returns A resolved IDBPDatabase handle ready for read/write operations
 * @throws Re-throws any error encountered during database opening after logging it
 */
export async function openStorageDB(): Promise<IDBPDatabase> {
  try {
    const db: IDBPDatabase = await openDB(DB_NAME, DB_VERSION, {
      upgrade(database: IDBPDatabase): void {
        if (!database.objectStoreNames.contains(STORE_NAME)) {
          Log.i(
            TAG,
            `openStorageDB(): Creating object store "${STORE_NAME}" with keyPath "key"`
          );
          const store = database.createObjectStore(STORE_NAME, {
            keyPath: "key"
          });
          store.createIndex(VALID_TILL_INDEX, VALID_TILL_INDEX, {
            unique: false
          });
          store.createIndex(EVICT_TILL_INDEX, EVICT_TILL_INDEX, {
            unique: false
          });
        }
      }
    });

    Log.d(
      TAG,
      `openStorageDB(): Database "${DB_NAME}" v${DB_VERSION} opened successfully`
    );
    return db;
  } catch (error: unknown) {
    Log.e(TAG, "openStorageDB()", error);
    throw error;
  }
}
